import { Check, CornerDownRight, Hand, Trash2, X } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ClaimButton } from "@/components/messages/claim-button";
import { TaskDueChip, TaskSourceTag } from "@/components/messages/task-card";
import { getAvatarColorClasses } from "@/lib/messages/avatar-color";
import { getThreadDisplayName, getThreadInitials } from "@/lib/messages/messages-helpers";
import { findGroupById, getGroupColor, getThreadGroupId } from "@/lib/thread-groups";
import { getAuthUser } from "@/lib/api/token-store";
import { cn } from "@/lib/utils";

function rowOwnerInitials(name) {
  const tokens = String(name || "")
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0][0]?.toUpperCase() || "?";
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
}

/**
 * One team-queue row (design 04-tasks-page): round checkbox (rose ring when overdue), title,
 * linked-thread chip (avatar + name + clinic dot), source tag, due/overdue chip, and a Claim
 * button while unclaimed. Once claimed, the owner avatar opens a small menu to take the task
 * over or clear the assignment (pass `onTakeOver` / `onRelease`).
 */
export function TaskRow({
  task,
  nowMs = Date.now(),
  groups = [],
  onToggle,
  onClaim,
  onTakeOver,
  onRelease,
  onDelete,
  onOpenThread,
}) {
  const me = String(getAuthUser()?.id || "").trim();
  const done = task.status === "done";
  const overdue = !done && task.dueAt ? new Date(task.dueAt).getTime() <= nowMs : false;
  const thread = task.thread;
  const threadId = String(task.threadId || "").trim();
  const { label: threadTitle } = thread ? getThreadDisplayName(thread) : { label: "" };
  const groupId = thread ? getThreadGroupId(thread) : "";
  const group = groupId ? findGroupById(groups, groupId) : null;
  const groupColor = group ? getGroupColor(group.colorId) : null;
  const unclaimed = !done && !String(task.assigneeId || "").trim();
  const assigneeIsMe = Boolean(task.assigneeId && me && task.assigneeId === me);
  const assigneeName = String(task.assignee?.displayName || "").trim();

  return (
    <div
      className={cn(
        "group/taskrow flex items-center gap-3 rounded-lg border bg-card px-3.5 py-2.5 transition",
        done ? "border-border opacity-75" : "border-border hover:border-indigo-200 hover:shadow-sm dark:hover:border-indigo-500/40",
      )}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={done}
        aria-label={done ? `Reopen task: ${task.title}` : `Complete task: ${task.title}`}
        onClick={() => onToggle?.(task.id, !done)}
        className={cn(
          "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          done
            ? "bg-emerald-500 text-white"
            : cn(
                "border-2 hover:border-emerald-500",
                overdue ? "border-rose-300 dark:border-rose-500/60" : "border-muted-foreground/40",
              ),
        )}
      >
        {done ? <Check className="h-2.5 w-2.5" strokeWidth={3} aria-hidden /> : null}
      </button>

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "truncate text-[13px] leading-snug",
            done ? "text-muted-foreground line-through" : "font-semibold text-foreground",
          )}
        >
          {task.title}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {thread ? (
            <button
              type="button"
              onClick={() => onOpenThread?.(threadId)}
              title={`Open conversation with ${threadTitle}`}
              className="inline-flex min-w-0 items-center gap-1 rounded text-[10.5px] text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CornerDownRight className="h-3 w-3 shrink-0" strokeWidth={2.25} aria-hidden />
              <Avatar className="h-4 w-4 shrink-0">
                <AvatarImage src={thread?.avatarUrl} />
                <AvatarFallback
                  className={cn("text-[8px] font-bold", getAvatarColorClasses(threadTitle || threadId))}
                >
                  {getThreadInitials(threadTitle)}
                </AvatarFallback>
              </Avatar>
              <span className="max-w-[180px] truncate font-medium">{threadTitle}</span>
              {group ? (
                <>
                  <span className={cn("h-1 w-1 shrink-0 rounded-full", groupColor?.dotClassName)} aria-hidden />
                  <span className="shrink-0">{group.name}</span>
                </>
              ) : null}
            </button>
          ) : null}
          <TaskSourceTag task={task} />
          <TaskDueChip task={task} nowMs={nowMs} />
          {done && task.doneByName ? (
            <span className="text-[9.5px] text-muted-foreground">{task.doneByName} · done</span>
          ) : null}
        </div>
      </div>

      {unclaimed && onClaim ? (
        <ClaimButton
          onClick={() => onClaim(task.id)}
          className="shrink-0 px-3 py-1.5 text-[11.5px] font-bold"
          title="Claim this task — it's yours"
        />
      ) : !done && task.assigneeId && (onTakeOver || onRelease) ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={
                assigneeIsMe ? "Claimed by you — change assignment" : `Claimed by ${assigneeName || "a teammate"} — change assignment`
              }
              title={
                assigneeIsMe
                  ? "Claimed by you — click to change"
                  : `Claimed by ${assigneeName || "a teammate"} — click to change`
              }
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold leading-none transition hover:ring-2 hover:ring-ring/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                assigneeIsMe ? "bg-indigo-600 text-white dark:bg-indigo-500" : "bg-muted text-muted-foreground",
              )}
            >
              {rowOwnerInitials(assigneeIsMe && !assigneeName ? "Me" : assigneeName)}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-48 p-1.5">
            <div className="px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {assigneeIsMe ? "Claimed by you" : `Claimed by ${assigneeName || "a teammate"}`}
            </div>
            {!assigneeIsMe && onTakeOver ? (
              <button
                type="button"
                onClick={() => onTakeOver(task.id)}
                className="flex w-full items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left text-xs transition hover:bg-muted/70"
              >
                <Hand className="h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden />
                Take over (assign to me)
              </button>
            ) : null}
            {onRelease ? (
              <button
                type="button"
                onClick={() => onRelease(task.id)}
                className="flex w-full items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left text-xs text-muted-foreground transition hover:bg-muted/70"
              >
                <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Clear assignee
              </button>
            ) : null}
          </PopoverContent>
        </Popover>
      ) : !done && (assigneeName || assigneeIsMe) ? (
        <span
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold leading-none",
            assigneeIsMe ? "bg-indigo-600 text-white dark:bg-indigo-500" : "bg-muted text-muted-foreground",
          )}
          title={assigneeIsMe ? "Claimed by you" : `Claimed by ${assigneeName}`}
        >
          {rowOwnerInitials(assigneeIsMe && !assigneeName ? "Me" : assigneeName)}
        </span>
      ) : null}
      {onDelete ? (
        <button
          type="button"
          aria-label={`Delete task: ${task.title}`}
          title="Delete this task permanently"
          onClick={() => onDelete(task.id)}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground/0 transition hover:bg-muted hover:text-destructive group-hover/taskrow:text-muted-foreground/70 focus-visible:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
